import { Cancel01Icon } from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import { toast } from "react-toastify";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent
} from "shared/components/Tooltip";
import { cx } from "shared/utils/classnames";
import {
  useClearConversationFlag,
  useUndoConversationFlagClear
} from "../api/hooks";
import { useInboxEmbed } from "../context/useInboxEmbed";
import type { ConversationFlagKind } from "../types";
import { CONTROL_TOOLTIP_CLASS } from "./controlPillStyles";

const CLEAR_FLAG_TOOLTIP =
  "Handled this outside Boom? Clears the flag for everyone on your team.";

const UNDO_TOAST_MS = 8000;

const FlagClearedToast = ({
  onUndo,
  closeToast
}: {
  onUndo: () => void;
  closeToast: () => void;
}) => (
  <div className="flex items-center gap-4 text-sm font-medium text-white">
    <span>Flag cleared for everyone</span>
    <button
      type="button"
      className="font-bold text-primary-400 hover:text-primary-300"
      onClick={() => {
        onUndo();
        closeToast();
      }}
    >
      Undo
    </button>
    <button
      type="button"
      aria-label="Dismiss"
      className="flex size-5 items-center justify-center text-white/60 hover:text-white"
      onClick={closeToast}
    >
      <HugeiconsIcon icon={Cancel01Icon} size={16} />
    </button>
  </div>
);

const showFlagClearedToast = (onUndo: () => void) =>
  toast(
    ({ closeToast }) => (
      <FlagClearedToast onUndo={onUndo} closeToast={closeToast} />
    ),
    {
      autoClose: UNDO_TOAST_MS,
      closeOnClick: false,
      icon: false,
      theme: "dark",
      className:
        "!mx-auto !min-h-0 !w-fit !rounded-lg !bg-alpha-800 !px-4 !py-2.5 shadow-lg",
      bodyClassName: "!m-0 !p-0"
    }
  );

export const ClearFlagButton = ({
  conversationId,
  kind
}: {
  conversationId: string;
  kind: ConversationFlagKind;
}) => {
  const embed = useInboxEmbed();
  const { mutateAsync: clearFlag, isPending } =
    useClearConversationFlag(conversationId);
  const { mutate: undoClear } = useUndoConversationFlagClear(conversationId);

  const onClick = () =>
    clearFlag(kind)
      .then(({ clear }) => showFlagClearedToast(() => undoClear(clear.id)))
      .catch(() => undefined);

  return (
    <Tooltip placement="bottom-end">
      <TooltipTrigger asChild>
        <button
          type="button"
          disabled={isPending}
          onClick={onClick}
          className="shrink-0 font-bold text-danger-700 hover:underline disabled:opacity-50"
        >
          Clear flag
        </button>
      </TooltipTrigger>
      <TooltipContent className={cx(CONTROL_TOOLTIP_CLASS, embed?.menuZClass)}>
        {CLEAR_FLAG_TOOLTIP}
      </TooltipContent>
    </Tooltip>
  );
};
